import debug from "debug";
import * as THREE from "three";

const log = debug("app:tt");

/**
 * Class for creating and managing a tooltip in a 3D scene.
 * The tooltip displays information about objects under the mouse cursor.
 * @category Experience
 * @subcategory Manipulation
 */
class Tooltip {
  /**
   * Constructs a Tooltip instance.
   * @param {Object} experience - The main application experience object.
   * @param {THREE.Scene} experience.scene - The 3D scene.
   * @param {THREE.Camera} experience.camera.instance - The camera instance used for rendering.
   * @param {Object} experience.eventHub - The event hub for managing custom events.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = experience.scene;
    this.camera = experience.camera.instance;
    this.eventHub = experience.eventHub;
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.tooltipElement = this.createTooltipElement();

    this.eventListener();
  }

  cleanup() {
    log("Cleaning up Tooltip...");

    // Remove event listeners
    this.eventHub.off("globalMouseMove", this.handleMouseMove);

    // Remove tooltip element from the DOM
    if (this.tooltipElement && this.tooltipElement.parentNode) {
      this.tooltipElement.parentNode.removeChild(this.tooltipElement);
    }

    // Clear references
    this.tooltipElement = null;
    this.scene = null;
    this.camera = null;
    this.eventHub = null;
    this.raycaster = null;
    this.mouse = null;

    log("Tooltip cleanup complete.");
  }

  /**
   * Creates the tooltip element and appends it to the document body.
   * @returns {HTMLDivElement} - The tooltip element.
   */
  createTooltipElement() {
    const tooltip = document.createElement("div");
    tooltip.style.position = "absolute";
    tooltip.style.backgroundColor = "#333";
    tooltip.style.color = "#fff";
    tooltip.style.padding = "5px 10px";
    tooltip.style.borderRadius = "5px";
    tooltip.style.pointerEvents = "none";
    tooltip.style.display = "none";
    document.body.appendChild(tooltip);
    return tooltip;
  }

  /**
   * Sets up event listeners for detecting mouse movements.
   */
  eventListener() {
    this.eventHub.on("globalMouseMove", this.handleMouseMove.bind(this));
  }

  findXMOParent(object) {
    while (object) {
      if (object.name === "XMO") {
        return object;
      }
      object = object.parent;
    }
    return null;
  }

  /**
   * Handles the mouse move event.
   * - Updates the mouse position.
   * - Checks for intersections with 3D objects in the scene.
   * - Displays or hides the tooltip based on intersections.
   * @param {MouseEvent} event - The mouse move event.
   */
  handleMouseMove(event) {
    if (event.buttons > 0) {
      this.hideTooltip();
      return;
    }
    this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);

    const intersects = this.raycaster.intersectObjects(
      this.scene.children,
      true
    );

    if (intersects.length > 0) {
      const intersectedObject = intersects[0].object;
      const xmoParent = this.findXMOParent(intersectedObject);
      log(`Intersected: ${intersectedObject.name || "Unnamed Object"}`);
      log(
        `Position: x=${intersectedObject.position.x}, y=${intersectedObject.position.y}, z=${intersectedObject.position.z}`
      );

      if (xmoParent && xmoParent.userData.partNumber) {
        this.showTooltip(
          event.clientX,
          event.clientY,
          xmoParent.userData.partNumber
        );
      } else if (xmoParent && xmoParent.userData.mode === "textbox") {
        this.showTooltip(event.clientX, event.clientY, "Right Click To Edit");
      } else {
        this.hideTooltip();
      }
    } else {
      this.hideTooltip();
    }
  }

  /**
   * Displays the tooltip at the specified screen coordinates with the given text.
   * @param {number} x - The X-coordinate on the screen.
   * @param {number} y - The Y-coordinate on the screen.
   * @param {string} text - The text to display inside the tooltip.
   */
  showTooltip(x, y, text) {
    this.tooltipElement.style.left = `${x + 10}px`;
    this.tooltipElement.style.top = `${y + 10}px`;
    this.tooltipElement.style.display = "block";
    this.tooltipElement.innerHTML = text;
  }

  /**
   * Hides the tooltip by setting its display property to "none".
   */
  hideTooltip() {
    this.tooltipElement.style.display = "none";
  }
}

export default Tooltip;
